import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import ProductTypesSection from '@/components/shared/ProductTypesSection';
import WhyChooseUsSection from '@/components/shared/WhyChooseUsSection';
import { ArrowRight, ShieldCheck, Star, Award, Boxes, Settings, FlaskConical, CheckCircle2, Factory, Heart, Sparkles, Wind, Palette, Smile, Leaf, Clock, Droplet, ShoppingBag, Package, Home } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-20 pb-24 md:pb-32 px-6 lg:px-12 bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] overflow-hidden">
          {/* Background Image / Overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-[60%] opacity-15 lg:opacity-100 z-0">
            <img 
              src="/Gambar Web/Botol 2.png" 
              alt="Cosmetic Products" 
              className="w-full h-full object-cover object-right md:object-center"
              style={{ WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 100%)', maskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 100%)' }}
            />
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-[65%] pt-6 md:pt-20 text-center md:text-left">
              <span className="inline-block py-1 px-3 md:py-1.5 md:px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[9px] md:text-[10px] font-bold tracking-widest uppercase rounded mb-4 border border-[#E6D5B8]/50">
                MAKLON PARFUM PREMIUM
              </span>
              <h1 className="text-[32px] leading-[1.15] md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 md:mb-6 text-[#302621]">
                Wujudkan Brand <br className="hidden md:block" /> Parfum <br className="hidden md:block" /> Impian Bersama Kami
              </h1>
              <p className="text-sm md:text-base text-[#7A695B] mb-8 md:mb-10 leading-relaxed max-w-sm md:max-w-md mx-auto md:mx-0 font-light">
                Setiap ide, sekecil apa pun, memiliki potensi untuk menjadi sesuatu yang luar biasa. Kami hadir memberikan solusi terpadu dengan fleksibilitas, profesionalisme, dan komitmen terhadap kualitas.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 mb-8 md:mb-12 justify-center md:justify-start">
                <Button size="lg" onClick={() => navigate('/pricing/parfum#form-konsultasi')} className="bg-[#D4AF37] hover:bg-[#B58B22] text-white h-12 md:h-14 px-6 md:px-8 text-[13px] md:text-base rounded-xl shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1 w-full sm:w-auto font-semibold">
                  Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/layanan/maklon-kosmetik')} className="border-[#302621] text-[#302621] bg-transparent hover:bg-[#302621] hover:text-white h-12 md:h-14 px-6 md:px-8 text-[13px] md:text-base rounded-xl transition-all hover:-translate-y-1 w-full sm:w-auto font-semibold">
                  Lihat Layanan Kami
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Row (Overlapping Hero) */}
        <section className="relative -mt-16 md:-mt-24 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            {/* Horizontal scrolling on mobile, grid on desktop */}
            <div className="flex overflow-x-auto pb-4 md:pb-0 md:grid md:grid-cols-4 gap-3 md:gap-6 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
              {[
                { count: "150+", label: "Brand Kosmetik", icon: <Award /> },
                { count: "500+", label: "Variant Unggulan", icon: <Boxes /> },
                { count: "100rb+", label: "Produk Terjual", icon: <Settings /> },
                { count: "99%", label: "Legalitas Terjamin", icon: <CheckCircle2 /> }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-4 md:p-8 rounded-2xl md:rounded-3xl shadow-xl shadow-[#302621]/5 flex flex-col items-center text-center gap-3 md:gap-4 hover:-translate-y-1 transition-transform min-w-[130px] w-[130px] md:w-auto snap-center border border-[#E6D5B8]/30 shrink-0">
                  <div className="w-10 h-10 md:w-14 md:h-14 shrink-0 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] shadow-sm border border-[#E6D5B8]/30">
                    {React.cloneElement(stat.icon, { className: "w-5 h-5 md:w-6 md:h-6" })}
                  </div>
                  <div>
                    <h3 className="text-xl md:text-3xl font-black text-[#302621] mb-0.5 md:mb-1">{stat.count}</h3>
                    <p className="text-[9px] md:text-xs text-[#7A695B] font-bold uppercase tracking-wider">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Layanan Kami */}
        <section className="py-16 md:py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-16">
              <div className="w-full lg:w-1/3 flex flex-col justify-center">
                <span className="inline-block py-1.5 px-3 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4 w-fit">
                  LAYANAN KAMI
                </span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#302621] mb-4 md:mb-6 leading-tight">Solusi Maklon Terintegrasi Untuk Brand Anda</h2>
                <p className="text-sm md:text-base text-[#7A695B] mb-8 md:mb-10 leading-relaxed font-light">Kami menyediakan layanan maklon parfum lengkap dengan dukungan tim ahli berpengalaman dan fasilitas produksi berstandar tinggi.</p>
                <Button onClick={() => navigate('/layanan/maklon-kosmetik')} className="bg-[#D4AF37] hover:bg-[#B58B22] text-white w-fit h-12 md:h-14 px-6 rounded-xl shadow-sm border-0 text-[13px] md:text-base font-semibold">
                  Lihat Semua Layanan <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="w-full lg:w-2/3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { title: "Pengembangan Formulasi", desc: "Layanan menyeluruh mulai dari perencanaan hingga pengembangan produk inovatif.", link: "/layanan/formulasi", img: "/Layanan Kami/konsultasi pengembangan.png" },
                    { title: "Legalitas Kosmetika", desc: "Fasilitasi proses perizinan BPOM, sertifikasi Halal, hingga HAKI untuk keamanan produk.", link: "/layanan/legalitas", img: "/Layanan Kami/Legal kosmetika.png" },
                    { title: "Rekomendasi Desain", desc: "Bantuan desain dan pilihan kemasan menarik yang memperkuat identitas brand Anda.", link: "/layanan/desain-kemasan", img: "/Layanan Kami/Rekomendasi Design.png" }
                  ].map((svc, idx) => (
                    <div key={idx} onClick={() => navigate(svc.link)} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-4 rounded-2xl flex flex-col text-left hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer group">
                      <div className="w-full aspect-[4/3] bg-gray-100 rounded-xl mb-6 overflow-hidden relative">
                        <img src={svc.img} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <h4 className="font-bold text-[#302621] mb-2 text-base group-hover:text-[#D4AF37] transition-colors">{svc.title}</h4>
                      <p className="text-xs text-[#7A695B] leading-relaxed mb-6 flex-grow">{svc.desc}</p>
                      <div className="text-[#D4AF37] font-bold text-xs uppercase tracking-wider flex items-center">
                        Selengkapnya <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mengapa Memilih Satala Dermatech? */}
        <WhyChooseUsSection 
          title={"Mengapa Memilih \n Satala Dermatech?"}
          description="Komitmen kami adalah memberikan hasil terbaik untuk pertumbuhan brand Anda."
          items={[
            { title: "Formulasi Teruji", desc: "Formula sudah teruji dan ditest pasar, riset jadi lebih cepat dan mudah.", icon: <Star /> },
            { title: "Proses Legalitas Cepat", desc: "Pengurusan BPOM, Halal & HAKI yang anti ribet dan sangat efisien.", icon: <ShieldCheck /> },
            { title: "Produksi Cepat", desc: "Dengan formula dan kemasan yang ada, tinggal pilih dan langsung proses.", icon: <Factory /> },
            { title: "MOQ Rendah", desc: "Kamu sudah bisa punya produk dengan brand kamu sendiri tanpa modal raksasa.", icon: <Boxes /> },
            { title: "RnD Handal", desc: "Tim RnD kami memiliki belasan tahun pengalaman di industri parfum.", icon: <FlaskConical /> },
            { title: "Banyak Pilihan Bibit", desc: "Tersedia pilihan bibit parfum mulai dari harga 500 ribu sampai 30 juta rupiah/kg.", icon: <Sparkles /> },
            { title: "Support Penjualan E-Commerce", desc: "Kami siap mensupport penuh persiapan dan strategi penjualan Anda di e-commerce.", icon: <ShoppingBag /> },
            { title: "Bibit Kreasi Perfumer", desc: "Koleksi bibit eksklusif yang diracik langsung oleh perfumer profesional.", icon: <Droplet /> }
          ]}
        />

        {/* Kategori Produk */}
        <ProductTypesSection 
          title="Kategori Produk yang Dapat Dimaklon" 
          subtitle="Berbagai kategori produk parfum yang dapat kami produksi untuk brand Anda." 
        />

        {/* Brand Logos */}
        <section className="py-12 md:py-20 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl text-center">
            <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/30 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded-full mb-4 border border-[#E6D5B8]/50">
              DIPERCAYA BANYAK BRAND
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-[#302621] mb-3 md:mb-4">Brand yang Telah Bermitra</h2>
            <p className="text-sm md:text-base text-[#7A695B] mb-10 md:mb-16 max-w-xl mx-auto">
              Bersama berbagai brand parfum, kami menghadirkan produk berkualitas dan bertumbuh bersama.
            </p>
            
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-8 items-start justify-items-center">
              {[
                { id: 1, name: "Simbiot", img: "/logo/1.jpg" },
                { id: 2, name: "Gamal", img: "/logo/2.jpg" },
                { id: 3, name: "Meet The Makers", img: "/logo/3.jpg" },
                { id: 4, name: "Meet Me At The Altar", img: "/logo/4.jpg" },
                { id: 5, name: "Manifest", img: "/logo/5.jpg" },
                { id: 6, name: "Maison Garnet", img: "/logo/6.jpg" },
                { id: 7, name: "Tomolab", img: "/logo/7.jpg" },
                { id: 8, name: "Valord", img: "/logo/8.jpg" },
                { id: 9, name: "Kokulu", img: "/logo/9.jpg" },
                { id: 10, name: "Foncé", img: "/logo/10.jpg" },
                { id: 11, name: "Bhirama Essentials", img: "/logo/11.jpg" },
                { id: 12, name: "Superdope!", img: "/logo/12.jpg" }
              ].map((brand) => (
                <div 
                  key={brand.id} 
                  className="w-full flex flex-col items-center group cursor-pointer"
                  onClick={() => navigate('/partner', { state: { scrollToBrand: brand.name } })}
                >
                  <div className="w-full aspect-square bg-white rounded-2xl md:rounded-3xl flex items-center justify-center p-3 md:p-6 mb-2 md:mb-4 shadow-sm border-2 border-transparent group-hover:border-[#D4AF37] group-hover:shadow-md transition-all duration-300">
                    <img src={brand.img} alt={brand.name} className="max-h-full max-w-[90%] object-contain mix-blend-multiply" />
                  </div>
                  <span className="text-[9px] md:text-sm font-bold text-[#302621] text-center px-1 leading-tight group-hover:text-[#D4AF37] transition-colors">{brand.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-[#EFE8D8] rounded-2xl md:rounded-3xl overflow-hidden relative shadow-sm border border-[#E6D5B8]/50">
              <div className="absolute right-0 top-0 bottom-0 w-full md:w-1/2 md:right-0 opacity-30 md:opacity-100 flex justify-end">
                <img src="/Paket Maklon/Paket Sedikit Landscape.png" alt="Products" className="max-w-full max-h-full object-contain object-right mask-image-gradient-left" />
              </div>
              <div className="relative z-10 p-8 md:p-16 w-full md:w-[55%] lg:w-1/2">
                <h2 className="text-2xl md:text-4xl font-bold text-[#302621] mb-3 md:mb-4 leading-tight">Siap Mewujudkan Brand <br className="hidden md:block"/> Parfum Anda?</h2>
                <p className="text-[#7A695B] text-sm md:text-lg leading-relaxed mb-6 md:mb-8 max-w-md">
                  Konsultasikan ide dan kebutuhan Anda bersama tim ahli kami. Kami siap membantu dari awal hingga produk siap dipasarkan.
                </p>
                <Button size="lg" onClick={() => navigate('/pricing/parfum#form-konsultasi')} className="bg-[#D4AF37] hover:bg-[#B58B22] text-white h-12 md:h-14 px-6 md:px-8 text-[13px] md:text-base rounded-xl shadow-md border-0 transition-transform hover:-translate-y-1 font-semibold w-full sm:w-auto">
                  Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>



        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
